<script setup lang="ts">
type Props = {
  hideCloseButton?: boolean;
  persistent?: boolean;
  width?: Number;
  customClassModal?: string;
  customClassWrapper?: string;
  showBackButton?: boolean;
  backButtonIconName?: string;
  mobileFullscreen?: boolean;
  ariaLabel?: string;
};

const {
  hideCloseButton = false,
  persistent = false,
  width = 450,
  customClassModal = '',
  customClassWrapper = '',
  mobileFullscreen = true,
  showBackButton = false,
  backButtonIconName = 'arrows/arrow_left',
  ariaLabel = undefined,
} = defineProps<Props>();

type Emits = {
  close: [];
  back: [];
};

const emit = defineEmits<Emits>();

const { isMobile } = useLayout();

const modalMaxWidthInPixels = computed(() => {
  if (isMobile.value && mobileFullscreen) {
    return `940px`;
  } else if (width) {
    return `${width}px`;
  } else {
    return '450px';
  }
});

function closeDialog() {
  isVisible.value = false;

  emit('close');
}

function onCloseButtonClick() {
  closeDialog();
}

function onBackButtonClick() {
  emit('back');
}

function onClickOutside() {
  if (persistent) {
    return;
  }

  closeDialog();
}

const isVisible = defineModel<boolean>('modelValue', {
  required: false,
  default: false,
});

// A11y (Handoff, resumo geral): "Modais com role=dialog, aria-modal, foco
// preso e retorno de foco ao fechar." ESC só fecha modais não-bloqueantes
// (persistent=false) — Tela 1 do handoff exige "sem dismiss".
const modalRef = ref<HTMLElement | null>(null);

const { trapFocus } = useModalA11y(modalRef, isVisible, () => {
  if (!persistent) closeDialog();
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      ref="modalRef"
      :aria-label="ariaLabel"
      aria-modal="true"
      :class="[$style.modal, customClassModal]"
      role="dialog"
      tabindex="-1"
      @click.self.stop="onClickOutside"
      @keydown="trapFocus"
    >
      <div
        :class="[
          $style.wrapper,
          isMobile && mobileFullscreen
            ? $style.wrapperFull
            : $style.wrapperWindow,
          customClassWrapper,
        ]"
      >
        <button
          v-if="showBackButton"
          :class="$style.backButton"
          @click="onBackButtonClick"
        >
          <NuxtIcon :name="backButtonIconName" />
        </button>
        <button
          v-if="!hideCloseButton"
          :class="$style.closeButton"
          @click="onCloseButtonClick"
        >
          <NuxtIcon name="close" />
        </button>

        <div :class="$style.inner">
          <slot />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style module lang="postcss">
.modal {
  @apply fixed top-0 left-0 w-full h-full bg-black/70 z-[1000] flex items-center justify-center;

  .backButton {
    @apply w-[40px] h-[40px] absolute top-2 left-2 z-30 text-auth-icon-close bg-auth-bg-close/90 p-2 rounded-[5px] font-medium transition-none flex justify-center items-center;

    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.4);

    svg {
      @apply text-auth-icon-close fill-auth-icon-close w-[1.2em] h-[1.2em];
    }

    @media (min-width: 769px) {
      @apply -left-[2px] -top-[2px];

      &:hover {
        @apply left-0 top-0;
      }
    }
  }

  .closeButton {
    @apply w-[40px] h-[40px] absolute top-2 right-2 z-30 text-auth-icon-close bg-auth-bg-close/90 p-2 rounded-[5px] font-medium transition-none flex justify-center items-center;

    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.4);

    svg {
      @apply text-auth-icon-close fill-auth-icon-close w-[1.2em] h-[1.2em];
    }

    @media (min-width: 769px) {
      @apply -right-[2px] -top-[2px];

      &:hover {
        @apply right-0 top-0;
      }
    }
  }

  .wrapper {
    @apply relative w-full md:h-auto mx-auto transform rounded-none sm:rounded-lg text-left align-middle shadow-xl bg-bg-primary/95 md:bg-bg-primary;

    max-width: v-bind(modalMaxWidthInPixels);

    @media (max-width: 768px) {
      @supports (backdrop-filter: blur(10px)) {
        backdrop-filter: blur(10px);
      }
    }
  }

  .wrapperFull {
    @apply h-full;
  }

  .wrapperWindow {
    @apply h-auto max-h-[90dvh];
  }

  .inner {
    @apply overflow-y-auto w-full h-full max-h-[100dvh];
  }
}
</style>
